/**
 * Bildirim ayarları — localStorage yardımcıları.
 * NotificationSettingsPopup ile WSContext arasında paylaşılır.
 */

export interface NotifSettings {
  soundEnabled: boolean;
  volume: number;          // 0–100
  events: {
    order_sent:    boolean;
    order_filled:  boolean;
    tp_filled:     boolean;
    drip_chunk:    boolean;
    kill_switch:   boolean;
    other:         boolean;
  };
}

const LS_KEY = 'raydium_notif_settings';

export const DEFAULT_SETTINGS: NotifSettings = {
  soundEnabled: true,
  volume: 60,
  events: {
    order_sent:   true,
    order_filled: true,
    tp_filled:    true,
    drip_chunk:   true,
    kill_switch:  true,
    other:        false,
  },
};

export function getNotifSettings(): NotifSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS, events: { ...DEFAULT_SETTINGS.events } };
    const parsed = JSON.parse(raw) as Partial<NotifSettings>;
    return {
      soundEnabled: parsed.soundEnabled ?? DEFAULT_SETTINGS.soundEnabled,
      volume:       parsed.volume       ?? DEFAULT_SETTINGS.volume,
      events: {
        order_sent:   parsed.events?.order_sent   ?? DEFAULT_SETTINGS.events.order_sent,
        order_filled: parsed.events?.order_filled ?? DEFAULT_SETTINGS.events.order_filled,
        tp_filled:    parsed.events?.tp_filled    ?? DEFAULT_SETTINGS.events.tp_filled,
        drip_chunk:   parsed.events?.drip_chunk   ?? DEFAULT_SETTINGS.events.drip_chunk,
        kill_switch:  parsed.events?.kill_switch  ?? DEFAULT_SETTINGS.events.kill_switch,
        other:        parsed.events?.other        ?? DEFAULT_SETTINGS.events.other,
      },
    };
  } catch {
    return { ...DEFAULT_SETTINGS, events: { ...DEFAULT_SETTINGS.events } };
  }
}

export function saveNotifSettings(s: NotifSettings): void {
  localStorage.setItem(LS_KEY, JSON.stringify(s));
}

/** Bir bildirim başlığının hangi event türüne girdiğini tespit eder. */
export type EventKey = keyof NotifSettings['events'];

export function detectEventKey(title: string): EventKey {
  if (title.includes('📤')) return 'order_sent';
  if (title.includes('✅ Emir')) return 'order_filled';
  if (title.includes('🎯') && title.includes('Drip')) return 'drip_chunk';
  if (title.includes('🎯') || title.includes('TP')) return 'tp_filled';
  if (title.includes('🛑')) return 'kill_switch';
  return 'other';
}

/** Bildirim sesli mi çalınmalı? */
export function shouldPlaySound(title: string): boolean {
  const s = getNotifSettings();
  if (!s.soundEnabled) return false;
  const key = detectEventKey(title);
  return !!s.events[key];
}

let _audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext {
  if (!_audioCtx) _audioCtx = new AudioContext();
  return _audioCtx;
}

/** Kısa bir "ding" sesi çal — volume 0–100. */
export function playNotifSound(volumeOverride?: number): void {
  try {
    const s = getNotifSettings();
    const vol = (volumeOverride ?? s.volume) / 100;
    const ctx  = getAudioCtx();

    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.2);

    gain.gain.setValueAtTime(vol * 0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch {
    // Ses desteklenmiyor — sessizce geç
  }
}
